import useDisableThirdPartyScripts from '@hooks/useDisableThirdPartyScripts'
import { User } from '@hooks/useUser'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useQueryClient } from 'react-query'

type AuthResolved = true | undefined

const enrichAndPushEvent = (event: Record<string, unknown>, user: User) => {
  window.dataLayer.push({
    ...event,
    userId: event?.userId ?? user?.sub,
  })
}

const Tracking: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const disableThirdPartyScripts = useDisableThirdPartyScripts()

  const [authResolved, setAuthResolved] = useState<AuthResolved>(() =>
    queryClient.getQueryData<AuthResolved>('auth-resolved')
  )

  const unsubscribeFnRef = useRef<() => void>()

  const updateAuthResolved = useCallback(
    (args: any) => {
      if (
        args?.action?.type === 'invalidate' &&
        args.query.queryKey === 'auth-resolved'
      ) {
        setAuthResolved(queryClient.getQueryData<AuthResolved>('auth-resolved'))
      }
    },
    [queryClient]
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(updateAuthResolved)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateAuthResolved, queryCache])

  useEffect(() => {
    if (!disableThirdPartyScripts && authResolved) {
      const user = queryClient.getQueryData<User>('user')
      if (window.eventQueueDataLayer) {
        window.eventQueueDataLayer.forEach((event) =>
          enrichAndPushEvent(event, user)
        )
        ;(window as any).eventQueueDataLayer = {
          push: (...events: Record<string, unknown>[]) => {
            events.forEach((event) => enrichAndPushEvent(event, user))
          },
          forEach: window.eventQueueDataLayer.forEach,
        }
      } else {
        console.error('eventQueueDataLayer is not defined! it should be!')
      }
    }
  }, [authResolved, queryClient, disableThirdPartyScripts])

  return null
}

export default Tracking
